import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Tree } from 'react-arborist';
import {
  Buildings, Package, UserCircle, UsersThree, CaretRight, CaretDown, HardDrives, MagnifyingGlass,
} from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import UserAssets from '../../../components/UserAssets';
import { listAssets, getUserClients } from '../api';
import { loadAssetHierarchy } from '../assetHierarchy';
import { ROLE_BADGE, STATUS_BADGE, Detail, fmtLabel } from '../ui';

const TYPE_ICON = { company: Buildings, product: Package, client: Buildings, member: UserCircle };

/**
 * Prototype: a single-screen master-detail explorer for the whole
 * Company -> Product -> Client -> Member hierarchy, instead of a click-through
 * chain of pages. Left pane is a react-arborist tree (lazy nothing yet — the
 * dataset is small enough to load eagerly); right pane shows whatever node is
 * selected. Existing flat pages (Companies/Team/Client/Member) are untouched.
 */
function Row({ node, style }) {
  const Icon = TYPE_ICON[node.data.type] || Package;
  const disabled = node.data.disabled;
  return (
    <div
      style={{
        ...style,
        display: 'flex', alignItems: 'center', gap: '6px', paddingRight: '8px',
        cursor: disabled ? 'default' : 'pointer',
        background: node.isSelected ? '#EFF6FF' : 'transparent',
        borderRadius: '6px', opacity: disabled ? 0.5 : 1,
      }}
      onClick={disabled ? undefined : node.handleClick}
    >
      {node.isInternal ? (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#9CA3AF', display: 'flex' }}
          aria-label={node.isOpen ? 'Collapse' : 'Expand'}>
          {node.isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        </button>
      ) : (
        <span style={{ width: '16px', flexShrink: 0 }} />
      )}
      <Icon size={15} color={node.isSelected ? '#002FA7' : '#6B7280'} style={{ flexShrink: 0 }} />
      <span style={{
        fontSize: '12px', fontWeight: node.isSelected ? '600' : '500',
        color: node.isSelected ? '#002FA7' : '#111827',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {node.data.name}
      </span>
    </div>
  );
}

function Breadcrumb({ node }) {
  if (!node) return null;
  const chain = [];
  let n = node;
  while (n) { chain.unshift(n); n = n.parent && !n.parent.isRoot ? n.parent : null; }
  return (
    <p style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '10px' }}>
      {chain.map((c, i) => (
        <span key={c.id}>
          {i > 0 && ' / '}
          <span style={{ color: i === chain.length - 1 ? '#374151' : '#9CA3AF', fontWeight: i === chain.length - 1 ? '600' : '400' }}>
            {c.data.name}
          </span>
        </span>
      ))}
    </p>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="hover-card" style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
      background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: '10px',
    }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px', background: tone.iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={17} weight="bold" color={tone.icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: '17px', fontWeight: '700', color: '#111827', lineHeight: 1.1 }}>{value}</p>
        <p style={{ fontSize: '11px', color: '#6B7280' }}>{label}</p>
      </div>
    </div>
  );
}

const TONE = {
  members: { bg: '#EFF6FF', iconBg: '#DBEAFE', icon: '#2563EB', border: '#BFDBFE' },
  infra: { bg: '#F5F3FF', iconBg: '#EDE9FE', icon: '#7C3AED', border: '#DDD6FE' },
  products: { bg: '#FFFBEB', iconBg: '#FEF3C7', icon: '#B45309', border: '#FDE68A' },
  assets: { bg: '#ECFDF5', iconBg: '#D1FAE5', icon: '#059669', border: '#A7F3D0' },
};

function DetailHeader({ icon: Icon, tone, name, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px', background: tone.iconBg, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={17} weight="bold" color={tone.icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700' }}>{name}</h3>
        {subtitle && <p style={{ fontSize: '12px', color: '#6B7280' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

function CompanyDetail({ data }) {
  return (
    <div>
      <DetailHeader icon={Buildings} tone={TONE.products} name={data.name} subtitle={data.raw?.description} />
      <StatCard icon={Package} label="Products" value={(data.children || []).length} tone={TONE.products} />
    </div>
  );
}

function ProductDetail({ data }) {
  const p = data.raw;
  return (
    <div>
      <DetailHeader icon={Package} tone={TONE.assets} name={data.name} subtitle={p?.description} />
      {!data.hasTeam ? (
        <div className="page-enter" style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '14px',
          background: '#F9FAFB', border: '1px dashed #E5E7EB', borderRadius: '10px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', background: '#F3F4F6', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UsersThree size={18} color="#9CA3AF" />
          </div>
          <p style={{ fontSize: '12px', color: '#9CA3AF' }}>No team associated with this product yet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
          <StatCard icon={UsersThree} label="Members" value={p?.member_count ?? 0} tone={TONE.members} />
          <StatCard icon={Package} label="Assets assigned" value={p?.assets_assigned ?? 0} tone={TONE.assets} />
          <div style={{ gridColumn: '1 / -1' }}>
            <Detail label="Key" value={p?.key} mono />
          </div>
        </div>
      )}
    </div>
  );
}

function ClientDetail({ data }) {
  const [infra, setInfra] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAssets({ client_id: data.clientId })
      .then((rows) => { if (!cancelled) setInfra(rows); })
      .catch(() => { if (!cancelled) setInfra([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [data.clientId]);

  return (
    <div>
      <DetailHeader icon={Buildings} tone={TONE.infra} name={data.name} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        <StatCard icon={UsersThree} label="Team members" value={(data.children || []).length} tone={TONE.members} />
        <StatCard icon={HardDrives} label="Infrastructure assets" value={loading ? '…' : infra.length} tone={TONE.infra} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
        <HardDrives size={16} weight="bold" color="#7C3AED" />
        <span style={{ fontSize: '12px', fontWeight: '600' }}>Infrastructure</span>
      </div>
      {loading ? (
        <p style={{ fontSize: '12px', color: '#6B7280' }}>Loading…</p>
      ) : infra.length === 0 ? (
        <div key={`empty-${data.clientId}`} className="page-enter" style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '14px',
          background: '#FAFAFF', border: '1px dashed #DDD6FE', borderRadius: '10px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', background: '#F5F3FF', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HardDrives size={18} color="#A78BFA" />
          </div>
          <div>
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>No infrastructure tracked yet</p>
            <p style={{ fontSize: '11px', color: '#9CA3AF' }}>
              Add domains, hosting, or servers for this client from its Infrastructure tab.
            </p>
          </div>
        </div>
      ) : (
        <div key={`list-${data.clientId}`} className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {infra.map((a) => (
            <div key={a.asset_id} className="hover-card explorer-infra-row" style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '8px', background: 'white',
            }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '7px', background: '#F5F3FF', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <HardDrives size={15} color="#7C3AED" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '12px', fontWeight: '600' }}>{a.name}</p>
                <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'capitalize' }}>{fmtLabel(a.asset_type)}</p>
              </div>
              <span className="badge" style={{ background: STATUS_BADGE[a.status] || '#F3F4F6', textTransform: 'capitalize' }}>
                {fmtLabel(a.status)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemberDetail({ data }) {
  const m = data.raw || {};
  const [liveClients, setLiveClients] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setClientsLoading(true);
    setLiveClients(null);
    if (!data.userId) {
      setClientsLoading(false);
      return undefined;
    }
    getUserClients(data.userId)
      .then((rows) => { if (!cancelled) setLiveClients(rows || []); })
      .catch(() => { if (!cancelled) setLiveClients([]); })
      .finally(() => { if (!cancelled) setClientsLoading(false); });
    return () => { cancelled = true; };
  }, [data.userId]);

  const clientLabel = (() => {
    if (clientsLoading) return 'Loading…';
    if (liveClients && liveClients.length) {
      return liveClients.map((c) => c.name).join(', ');
    }
    if ((m.client_names || []).length) return m.client_names.join(', ');
    if ((m.client_ids || []).length) return `${m.client_ids.length} client(s)`;
    return 'None (product-level)';
  })();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        {m.picture ? (
          <img src={m.picture} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <UserCircle size={36} color="#9CA3AF" />
        )}
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: '700' }}>{m.name}</h3>
          <p style={{ fontSize: '12px', color: '#6B7280' }}>{m.designation || m.email}</p>
        </div>
        <span className="badge" style={{ background: ROLE_BADGE[m.org_role] || '#F3F4F6', fontSize: '11px', textTransform: 'capitalize', marginLeft: 'auto' }}>
          {m.org_role}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '10px' }}>
        <Detail label="Email" value={m.email} />
        <Detail label="Client(s)" value={clientLabel} />
        <Detail label="Assets assigned" value={m.asset_count != null ? String(m.asset_count) : '—'} />
      </div>
      <UserAssets
        key={`${data.userId}:${data.filterClientId || 'all'}`}
        userId={data.userId}
        editable
        clientFilter={data.filterClientId}
      />
    </div>
  );
}

function DetailPane({ node }) {
  if (!node) {
    return <EmptyState icon={Package} title="Nothing selected" description="Pick a company, product, client, or member from the tree." />;
  }
  const { data } = node;
  return (
    <div key={node.id} className="page-enter">
      <Breadcrumb node={node} />
      {data.type === 'company' && <CompanyDetail data={data} />}
      {data.type === 'product' && <ProductDetail data={data} />}
      {data.type === 'client' && <ClientDetail data={data} />}
      {data.type === 'member' && <MemberDetail data={data} />}
    </div>
  );
}

function ExplorerPage() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const companyNodes = await loadAssetHierarchy();
      companyNodes.forEach((company) => {
        (company.children || []).forEach((product) => {
          if (!product.hasTeam) product.disabled = true;
        });
      });
      setTree(companyNodes);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchMatch = useMemo(() => (node, term) => (
    (node.data.name || '').toLowerCase().includes(term.toLowerCase())
  ), []);

  if (loading) return <SkeletonBlock />;
  if (error) return <EmptyState icon={Buildings} title="Could not load" description={error} />;

  return (
    <div data-testid="asset-explorer" style={{ display: 'flex', gap: '14px', height: 'calc(100vh - 220px)', minHeight: '480px' }}>
      <div style={{
        width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column',
        background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '4px 8px', marginBottom: '8px' }}>
          <MagnifyingGlass size={14} color="#9CA3AF" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter…"
            style={{ border: 'none', outline: 'none', fontSize: '12px', flex: 1 }}
          />
          <button
            type="button"
            onClick={load}
            title="Refresh tree"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#002FA7', padding: '0 4px' }}
          >
            Refresh
          </button>
        </div>
        {tree.length === 0 ? (
          <EmptyState icon={Buildings} title="Nothing yet" description="Add companies and products to see them here." />
        ) : (
          <div style={{ flex: 1 }}>
            <Tree
              data={tree}
              openByDefault={false}
              width="100%"
              height={560}
              rowHeight={28}
              indent={16}
              searchTerm={query}
              searchMatch={searchMatch}
              disableDrag disableDrop disableEdit
              onSelect={(nodes) => setSelected(nodes[0] || null)}
            >
              {Row}
            </Tree>
          </div>
        )}
      </div>

      <div style={{ flex: 1, background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', overflowY: 'auto' }}>
        <DetailPane node={selected} />
      </div>
    </div>
  );
}

export default ExplorerPage;
